import { useEffect, useLayoutEffect, useRef, useState } from "react";

/** Fractional return (0.2243) -> "+22.43%". */
export function formatPct(x: number | null | undefined, digits = 2): string {
  if (x == null || Number.isNaN(x)) return "—";
  const v = x * 100;
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(digits)}%`;
}

export function formatMoney(x: number | null | undefined, digits = 2): string {
  if (x == null || Number.isNaN(x)) return "—";
  return `$${x.toFixed(digits)}`;
}

export function returnColor(x: number | null | undefined): string {
  if (x == null || x === 0) return "text-muted";
  return x > 0 ? "text-gain" : "text-loss";
}

export function returnBgClass(x: number | null | undefined): string {
  if (x == null || x === 0) return "bg-ink-700/40";
  return x > 0 ? "bg-gain/10" : "bg-loss/10";
}

/** Run a callback on an interval; pass null to pause. */
export function useInterval(cb: () => void, ms: number | null): void {
  const saved = useRef(cb);
  useEffect(() => {
    saved.current = cb;
  }, [cb]);
  useEffect(() => {
    if (ms == null) return;
    const id = setInterval(() => saved.current(), ms);
    return () => clearInterval(id);
  }, [ms]);
}

export interface PollState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  refresh: () => void;
}

/** Poll an async fetcher every `ms`; returns latest data + a manual refresh. */
export function usePoll<T>(
  fetcher: () => Promise<T>,
  ms: number,
  deps: unknown[] = [],
): PollState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  const run = useRef(async () => {
    try {
      const d = await fetcherRef.current();
      setData(d);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    let active = true;
    // Reset on dep change (e.g. a new manager name) so consumers fall back to
    // their loading/empty branch instead of rendering the previous result.
    setData(null);
    setError(null);
    setLoading(true);
    const tick = async () => {
      if (active) await run.current();
    };
    tick();
    const id = setInterval(tick, ms);
    return () => {
      active = false;
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ms, ...deps]);

  return { data, error, loading, refresh: () => run.current() };
}

/** Smoothly animate a number toward `target` for lively count-ups on stream. */
export function useCountUp(target: number, ms = 600): number {
  const [value, setValue] = useState(target);
  const from = useRef(target);
  const start = useRef<number | null>(null);
  const raf = useRef<number | null>(null);

  useEffect(() => {
    from.current = value;
    start.current = null;
    const step = (t: number) => {
      if (start.current === null) start.current = t;
      const p = Math.min(1, (t - start.current) / ms);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(from.current + (target - from.current) * eased);
      if (p < 1) raf.current = requestAnimationFrame(step);
    };
    raf.current = requestAnimationFrame(step);
    return () => {
      if (raf.current) cancelAnimationFrame(raf.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, ms]);

  return value;
}

/** Live ET wall-clock string "HH:MM:SS", updating each second. */
export function useEtClock(): string {
  const [s, setS] = useState(() => etClockString());
  useInterval(() => setS(etClockString()), 1000);
  return s;
}

export function etClockString(d: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(d);
}

/**
 * FLIP list animation: rows smoothly slide to their new position when the order
 * changes (e.g. the leaderboard re-ranks after a quote update). Returns a ref
 * factory — spread `ref={register(id)}` onto each row keyed by a stable id.
 */
export function useFlipList<T extends string | number>() {
  const nodes = useRef(new Map<T, HTMLElement>());
  const prevRects = useRef(new Map<T, DOMRect>());

  useLayoutEffect(() => {
    nodes.current.forEach((el, id) => {
      const prev = prevRects.current.get(id);
      const next = el.getBoundingClientRect();
      if (prev) {
        const dy = prev.top - next.top;
        if (Math.abs(dy) > 1) {
          el.style.transition = "none";
          el.style.transform = `translateY(${dy}px)`;
          void el.offsetHeight; // force reflow so the start position takes
          requestAnimationFrame(() => {
            el.style.transition = "transform 650ms cubic-bezier(.2,.8,.2,1)";
            el.style.transform = "";
          });
        }
      }
    });
    const m = new Map<T, DOMRect>();
    nodes.current.forEach((el, id) => m.set(id, el.getBoundingClientRect()));
    prevRects.current = m;
  });

  return (id: T) => (el: HTMLElement | null) => {
    if (el) nodes.current.set(id, el);
    else nodes.current.delete(id);
  };
}

/**
 * Returns a flash animation class for ~0.9s whenever `value` changes direction
 * (green flash up, red flash down). Used to pulse cells on quote updates.
 */
export function useFlash(value: number): string {
  const prev = useRef(value);
  const [cls, setCls] = useState("");
  useEffect(() => {
    const changed = value !== prev.current;
    const up = value > prev.current;
    prev.current = value;
    if (!changed) return;
    setCls(up ? "animate-flashgain" : "animate-flashloss");
    const id = setTimeout(() => setCls(""), 950);
    return () => clearTimeout(id);
  }, [value]);
  return cls;
}

const MONTHS_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

/**
 * A chart timestamp ("2026-07-02T12:30" or a full ISO) -> "Jul 2". Reads the
 * date portion directly to sidestep timezone drift at day boundaries.
 */
export function formatDateTick(t: string): string {
  const [y, m, d] = t.slice(0, 10).split("-").map(Number);
  if (!m || !d) return "";
  return `${MONTHS_SHORT[m - 1]} ${d}`;
}

export function formatTimeFromISO(iso: string): string {
  try {
    return etClockString(new Date(iso));
  } catch {
    return "—";
  }
}
